import React from 'react';
import { Briefcase, MapPin, Banknote, Calendar, Users } from 'lucide-react';
import { jobVacancies } from '../data/jobVacancies';

const JobVacancyPage = ({ onApply }) => {

  return (
    <div className="bg-blue-50 min-h-screen transition-colors">
      {/* Header Section */}
      <section className="bg-gradient-to-br from-blue-700 via-blue-600 to-blue-700 py-16 relative overflow-hidden transition-colors">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-blue-200 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-300 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="inline-block bg-blue-200 text-blue-900 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <Users className="w-4 h-4 inline mr-2" />
            Bergabung dengan Tim Kami
          </div>
          <h1 className="text-3xl md:text-5xl font-display font-bold text-blue-50 mb-4 leading-tight">
            Karir di AIXpert
          </h1>
          <p className="text-lg md:text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
            Bergabunglah dengan kami untuk membangun masa depan pendidikan Data Science & AI di Indonesia!
          </p>
        </div>
      </section>

      {/* Job Listings */}
      <section className="py-12 bg-blue-50 transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 text-center">
            <h2 className="text-2xl md:text-3xl font-display font-bold text-blue-900 mb-2">
              {jobVacancies.length} Posisi Tersedia
            </h2>
            <p className="text-blue-600">Temukan peran yang cocok untuk Anda</p>
          </div>

          <div className="space-y-5">
            {jobVacancies.map((job) => (
              <div key={job.id} className="bg-white rounded-2xl border-2 border-blue-200 hover:border-blue-400 hover:shadow-2xl transition-all p-6">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1">
                    {/* Job Header */}
                    <div className="flex items-start gap-4 mb-4">
                      <div className="p-3 bg-blue-100 rounded-xl">
                        <Briefcase className="w-7 h-7 text-blue-700" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl md:text-2xl font-bold text-blue-900 mb-2">{job.title}</h3>
                        <div className="flex flex-wrap gap-2">
                          <span className="bg-blue-100 text-blue-800 text-sm px-4 py-1.5 rounded-full font-semibold">
                             {job.category}
                          </span>
                          <span className="bg-blue-300 text-blue-900 text-sm px-4 py-1.5 rounded-full font-semibold">
                             {job.type}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Job Meta */}
                    <div className="grid md:grid-cols-3 gap-4 mb-4 bg-blue-50 p-4 rounded-xl">
                      <div className="flex items-center text-sm text-blue-700 font-medium">
                        <MapPin className="w-4 h-4 mr-2 text-blue-500" />
                        {job.location}
                      </div>
                      <div className="flex items-center text-sm text-blue-700 font-medium">
                        <Banknote className="w-4 h-4 mr-2 text-blue-500" />
                        {job.salary}
                      </div>
                      <div className="flex items-center text-sm text-blue-700 font-medium">
                        <Calendar className="w-4 h-4 mr-2 text-blue-500" />
                        Posted {job.posted}
                      </div>
                    </div>

                    {/* Job Description */}
                    <p className="text-blue-700 mb-4 text-base leading-relaxed">{job.description}</p>

                    {/* Expandable Details */}
                    <details className="group">
                      <summary className="cursor-pointer text-blue-700 font-semibold hover:text-blue-900 mb-2 bg-blue-50 px-4 py-2 rounded-lg inline-block transition">
                         Lihat Detail Lengkap →
                      </summary>

                      <div className="mt-4 space-y-4 pl-4 border-l-4 border-blue-300 bg-blue-50 p-4 rounded-r-xl">
                        {/* Responsibilities */}
                        <div>
                          <h4 className="font-bold text-blue-900 mb-3 text-base"> Tanggung Jawab:</h4>
                          <ul className="space-y-2 text-blue-700 text-sm">
                            {job.responsibilities.map((item, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <span className="text-blue-500 mt-0.5">•</span>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Requirements */}
                        <div>
                          <h4 className="font-bold text-blue-900 mb-3 text-base"> Requirements:</h4>
                          <ul className="space-y-2 text-blue-700 text-sm">
                            {job.requirements.map((item, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <span className="text-blue-500 mt-0.5">•</span>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Benefits */}
                        <div>
                          <h4 className="font-bold text-blue-900 mb-3 text-base"> Benefits:</h4>
                          <ul className="space-y-2 text-blue-700 text-sm">
                            {job.benefits.map((item, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <span className="text-blue-500 mt-0.5">•</span>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </details>
                  </div>

                  {/* Apply Button */}
                  <div className="md:ml-4 flex items-start">
                    <button
                      onClick={() => onApply(job)}
                      className="w-full md:w-auto bg-blue-700 text-blue-50 px-8 py-4 rounded-xl hover:bg-blue-800 transition-all font-bold text-base shadow-lg hover:shadow-xl whitespace-nowrap"
                    >
                      Lamar Sekarang
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {jobVacancies.length === 0 && (
            <div className="text-center py-12 bg-white rounded-2xl border-2 border-blue-200">
              <p className="text-blue-600 text-lg">Tidak ada posisi tersedia saat ini.</p>
            </div>
          )}
        </div>
      </section>

      {/* Why Join Us Section */}
      <section className="py-16 bg-white transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-12 text-blue-900">
            Mengapa Bergabung dengan Kami?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-blue-50 rounded-2xl border-2 border-blue-200 hover:border-blue-400 hover:shadow-lg transition">
              <div className="text-6xl mb-4"></div>
              <h3 className="text-xl font-bold mb-3 text-blue-900">Impact & Growth</h3>
              <p className="text-blue-700 leading-relaxed">
                Bantu ribuan orang transformasi karir sambil mengembangkan skill Anda sendiri
              </p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-2xl border-2 border-blue-200 hover:border-blue-400 hover:shadow-lg transition">
              <div className="text-6xl mb-4"></div>
              <h3 className="text-xl font-bold mb-3 text-blue-900">Collaborative Team</h3>
              <p className="text-blue-700 leading-relaxed">
                Bekerja dengan tim yang passionate, supportive dan berbagi misi yang sama
              </p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-2xl border-2 border-blue-200 hover:border-blue-400 hover:shadow-lg transition">
              <div className="text-6xl mb-4"></div>
              <h3 className="text-xl font-bold mb-3 text-blue-900">Flexibility</h3>
              <p className="text-blue-700 leading-relaxed">
                Remote-friendly, flexible hours, dan work-life balance yang dijaga
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default JobVacancyPage;
